/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Link } from "@farmlands/shadcn-ui";
import { FC, type JSX } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import TestIds from "../../../../testIds";
import { getStringFromArray } from "../../../../util/formatting";
import {
  selectAllFarmerAccounts,
  selectIsCEUser,
  selectLoginEmail,
  selectUserContactDetails,
} from "../../../azure-b2c/storage";
import { PERSONAL_DETAILS_ROUTES } from "../../personal-details";
import InitialsCircle from "../InitialsCircle/InitialsCircle";

const ContactProfileHeader: FC = (): JSX.Element | undefined => {
  const navigate = useNavigate();
  const farmlandsAccounts = useSelector(selectAllFarmerAccounts);
  const accountEmail = useSelector(selectLoginEmail);
  const isCEUser = useSelector(selectIsCEUser);
  const userDetails = useSelector(selectUserContactDetails);
  const contactName = getStringFromArray([userDetails?.firstName, userDetails?.lastName]);

  if (!farmlandsAccounts) {
    return undefined;
  }

  return (
    <div
      className="fl-mobile-safe-area-support bg-forest-green-50"
      data-testid={TestIds.feat.account.userAccount.contactProfileHeader.component}
      aria-label={`Profile for ${contactName}`}
    >
      <div className="flex flex-col items-center gap-2 px-4 py-6">
        <InitialsCircle fullName={[userDetails?.firstName, userDetails?.lastName]} />

        <div className="flex flex-col items-center">
          <span
            className="text-center text-md leading-md font-bold text-neutral-800"
            data-testid={TestIds.feat.account.userAccount.contactProfileHeader.contactName}
          >
            {contactName}
          </span>

          <span
            className="text-center text-2xs leading-xs tracking-[0.07px] text-neutral-800"
            data-testid={TestIds.feat.account.userAccount.contactProfileHeader.email}
          >
            {accountEmail}
          </span>
        </div>

        {!isCEUser && (
          <Link asChild size="2xs">
            <button
              type="button"
              onClick={() => navigate(PERSONAL_DETAILS_ROUTES.BASE)}
              data-testid={
                TestIds.feat.account.userAccount.contactProfileHeader.viewPersonalDetails
              }
            >
              View Personal Details
            </button>
          </Link>
        )}
      </div>
    </div>
  );
};

export default ContactProfileHeader;
